// =============================================================================
// Admin Analytics — deterministic mock dataset (Activation)
// =============================================================================
// A seeded, stable pool of providers with an invite → login funnel, spread
// across the last ~90 days relative to "today". The mock adapter aggregates
// over this pool, so the global date filter and the per-widget time overrides
// produce genuinely different numbers (not canned constants). Seeded so renders
// and reloads are identical — important for a review-able prototype.
//
// All PII is fake.
// =============================================================================
import { format, startOfDay, subDays } from "date-fns";

const ISO = "yyyy-MM-dd";

/** mulberry32 — tiny deterministic PRNG so the dataset never shifts between renders. */
const seedRng = (seed: number) => {
  let a = seed;
  return () => {
    a = Math.trunc(a);
    a = Math.trunc(a + 0x6d2b79f5);
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const PROVIDER_NAMES = [
  "Dr. Amara Okafor",
  "Marcus Bennett",
  "Priya Raman",
  "Sofia Delgado",
  "Daniel Whitfield",
  "Hannah Liu",
  "Jared Coleman",
  "Naomi Castillo",
  "Ethan Park",
  "Rebecca Sayed",
  "Olivia Trent",
  "Kwame Mensah",
  "Isabella Romano",
  "Tyler Goodwin",
];

interface ProviderRecord {
  providerId: string;
  name: string;
  email: string;
  invitedAt: string;
  hasLoggedIn: boolean;
  lastLoginAt?: string;
  /** Invite link is older than 30 days and never accepted. */
  inviteExpired: boolean;
}

interface AnalyticsDataset {
  referenceDate: Date;
  providers: ProviderRecord[];
}

const isoDate = (d: Date) => format(d, ISO);

const emailFor = (name: string) =>
  `${name
    .replace(/^Dr\.\s+/, "")
    .toLowerCase()
    .replace(/[^a-z]+/g, ".")
    .replace(/^\.|\.$/g, "")}@clinic.example`;

const generateDataset = (referenceDate: Date): AnalyticsDataset => {
  const rng = seedRng(14742026);

  // Providers (14 BCBAs) with an invite + login funnel.
  const providers: ProviderRecord[] = PROVIDER_NAMES.map((name, i) => {
    const invitedDaysAgo = Math.floor(rng() * 90) + 3;
    const invitedAt = subDays(referenceDate, invitedDaysAgo);
    // ~70% have logged in. Of those who haven't, the older invites are "expired".
    const hasLoggedIn = rng() > 0.3;
    const inviteExpired = !hasLoggedIn && invitedDaysAgo > 30;
    const lastLoginAt = hasLoggedIn
      ? subDays(referenceDate, Math.floor(rng() * Math.min(invitedDaysAgo, 21)))
      : undefined;
    return {
      providerId: `prov-${i + 1}`,
      name,
      email: emailFor(name),
      invitedAt: isoDate(invitedAt),
      hasLoggedIn,
      lastLoginAt: lastLoginAt ? isoDate(lastLoginAt) : undefined,
      inviteExpired,
    };
  });

  return { referenceDate, providers };
};

// Computed once per session, anchored to the start of today.
export const dataset: AnalyticsDataset = generateDataset(startOfDay(new Date()));
